<template>
  <Page data-cy="page">
    <template #header>
      <div
      class="d-flex flex-row flex-wrap align-items-center pb-0 mb-3 justify-content-between page-header-content"
    >
      <div class="d-flex align-items-center">
        <h1 class="page-header-1">Token Export</h1>
      </div>
    </div>
    </template>
    <Card headless :card-style="{ maxWidth: '600px' }">
      <template #body>
        <div>
          <h5 class="mb-3">Migrating from GPI to Stax?</h5>
          <p>
            Please export your token file so you are ready to migrate your
            tokens from GPI to Stax. Once exported and saved, you’ll want to
            take the following steps to initiate your transfer:
          </p>
          <ol>
            <li>
              Submit the following form to notify GPI of your intent to migrate:
              <a target="_blank" href="https://openedgepayments.com/chdexport/"
                >https://openedgepayments.com/chdexport/</a
              >
            </li>
            <li>
              Upon this form being submitted, a case is created to begin work by
              GPI, who will be in touch with you with additional steps.
            </li>
            <li>
              You will be required to complete the following deliverables for
              GPI:
              <ol type="a">
                <li>Data Release Agreement</li>
                <li>
                  A transfer fee ($499 per transfer as of August 2023 - this
                  amount may change over time)
                </li>
              </ol>
            </li>
            <li>
              Send your token export file to GPI indicating which tokens you
              wish to transfer to Stax.
            </li>
            <li>
              GPI will initiate the token transfer with the new processor with
              an expected completion time of 2 weeks, depending on requirements
              from Stax.
            </li>
            <li>
              Once the transfer has been initiated, please contact a Sera
              onboarder or Customer Service contact, who will notify Stax on
              your behalf that tokens are available to migrate.
            </li>
            <li>
              Once Stax is notified, they will process the tokens and notify
              Sera upon completion.
            </li>
            <li>
              A Sera onboarder will then update your account with the new tokens
              from Stax on our end and notify you of any tokens that were unable
              to be migrated.
            </li>
          </ol>
          <div class="text-right">
            <Button type="primary" icon="fas fa-download" @click="toggleOpen">
              Export Tokens
            </Button>
          </div>
        </div>
      </template>
    </Card>

    <Modal
      v-if="modalOpen"
      title="Export Tokens"
      :saving="saving"
      saving-text="Generating..."
      submit-btn-text="Generate Export"
      @hide-modal="toggleOpen"
      @submit="handleSubmit"
    >
      <div class="form-container">
        <Select
          v-model="processor"
          :options="processorOptions"
          :class="{ 'is-invalid': validationErrors.processor }"
          label="Export tokens from"
          data-cy="processor-select"
        />
        <span
          v-if="validationErrors.processor"
          class="text-danger error-text"
          >{{ validationErrors.processor }}</span
        >
      </div>
    </Modal>
  </Page>
</template>

<script setup lang="ts">
import { ref } from "vue";
import { useStore } from "vuex";

import Button from "@/components/Button.vue";
import Card from "@/components/Card.vue";
import Modal from "@/components/Modal.vue";
import Page from "@/components/Page.vue";

const store = useStore();
const props = defineProps<{ processorOptions: Array<{ k: string; v: any }>; tokenExportPath: string; }>();

const modalOpen = ref(false);
const saving = ref(false);
const processor = ref(
  props.processorOptions.find((obj) => obj.label === "GPI")?.value
);
const validationErrors = ref({
  processor: ""
});

const toggleOpen = (newValue) => {
  modalOpen.value = newValue !== undefined ? newValue : !modalOpen.value;
};

const handleSubmit = async () => {
  validationErrors.value.processor = "";
  if (!processor.value) {
    validationErrors.value.processor =
      "Please choose a processor you wish to export tokens from";
    return;
  }

  saving.value = true;

  try {
    const responseData = await store.dispatch(
      "createPaymentMethodTokenExport",
      {
        processor: processor.value,
        url: props.tokenExportPath
      }
    );

    toggleOpen(false);

    downloadCsv(responseData.download_url);
  } catch (e) {
    // do nothing
  }

  saving.value = false;
};

const downloadCsv = (url) => {
  const link = document.createElement("a");
  link.href = url;
  link.style.display = "none";
  link.download = "Token Export";

  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};
</script>

<style lang="scss" scoped></style>
